import React, { useState } from 'react';

import Button from '~/components/core/Atomic/Buttons/Button';
import CollapsibleWrapper from '~/components/core/Collapsible/CollapsibleWrapper';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import StatusChip from '~/components/core/StatusChip';
import SortableTable from '~/components/core/Tables/SortableTable';
import Text from '~/components/core/TextComponents/Text';
import { serverDateTimeToLocal } from '~/DateTimeUtils';
import { reportAxiosError } from '~/Utils';

import { STATUS_TO_CHIP } from './constants';
import RunProgressDialog from './RunProgressDialog';
import type { AiClaimImportRunListItem, AiClaimImportRunListResponse, AiClaimImportRunProgress } from './types';

interface AiClaimImportCardProps {
  subOrganizationsEnabled: boolean;
  onImportBySubOrgClick: () => void;
  onImportByClaimsClick: () => void;
  runList: AiClaimImportRunListResponse | undefined;
  isRunListLoading: boolean;
  fetchRunProgress: (runId: string) => Promise<AiClaimImportRunProgress>;
}

const AiClaimImportCard: React.FC<AiClaimImportCardProps> = ({
  subOrganizationsEnabled,
  onImportBySubOrgClick,
  onImportByClaimsClick,
  runList,
  isRunListLoading,
  fetchRunProgress,
}) => {
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isProgressDialogOpen, setIsProgressDialogOpen] = useState(false);
  const [selectedProgress, setSelectedProgress] = useState<AiClaimImportRunProgress | null>(null);
  const [isProgressLoading, setIsProgressLoading] = useState(false);

  const handleViewDetails = async (runId: string) => {
    setIsProgressDialogOpen(true);
    setIsProgressLoading(true);
    try {
      const progress = await fetchRunProgress(runId);
      setSelectedProgress(progress);
    } catch (error) {
      await reportAxiosError(error);
      setIsProgressDialogOpen(false);
    } finally {
      setIsProgressLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setIsProgressDialogOpen(false);
    setSelectedProgress(null);
  };

  const columns = [
    {
      id: 'created_at',
      label: 'Date',
      specialCell: (row: AiClaimImportRunListItem) => serverDateTimeToLocal(row.created_at),
    },
    { id: 'initiated_by_user_name', label: 'Triggered By' },
    {
      id: 'status',
      label: 'Status',
      specialCell: (row: AiClaimImportRunListItem) => (
        <StatusChip status={STATUS_TO_CHIP[row.status] || StatusChip.STATUSES.PENDING} />
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      disableSort: true,
      specialCell: (row: AiClaimImportRunListItem) => (
        <Button variant="text" color="primary" size="small" onClick={() => handleViewDetails(row.run_id)}>
          View Details
        </Button>
      ),
    },
  ];

  const rows = (runList?.runs || []).map((run) => ({ ...run, id: run.run_id }));

  return (
    <>
      <div className="border-gray-300 flex flex-col rounded-lg border bg-white p-16">
        <div className="flex items-center justify-between">
          <div>
            <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
              Load open claims
            </Text>
            <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="mt-4">
              Choose the method for importing claims data. You can select sub-organizations or upload a file containing
              specific claims.
            </Text>
          </div>
          <div className="flex gap-8 self-end">
            <Button variant="outlined" color="primary" onClick={onImportByClaimsClick}>
              Select Claims
            </Button>
            {subOrganizationsEnabled && (
              <Button variant="contained" color="primary" onClick={onImportBySubOrgClick}>
                Select Sub-Orgs
              </Button>
            )}
          </div>
        </div>

        <div className="mt-16">
          <CollapsibleWrapper
            title={
              <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.LINK}>
                {isHistoryOpen ? 'Close claim loading history' : 'Show claim loading history'}
              </Text>
            }
            defaultState={false}
            onCollapse={setIsHistoryOpen}
            chevronContainerClassName="hidden"
            hideActionsOnCollapse
            noBackground
            noBorder
          >
            <LoadingSwitch isLoading={isRunListLoading} isError={false}>
              <SortableTable columns={columns} rows={rows} keepRowsOrder />
            </LoadingSwitch>
          </CollapsibleWrapper>
        </div>
      </div>

      <RunProgressDialog
        open={isProgressDialogOpen}
        onClose={handleCloseDialog}
        progress={selectedProgress}
        isLoading={isProgressLoading}
      />
    </>
  );
};

export default AiClaimImportCard;
